// Copyright 2023, Durable
import React, { ReactElement, useEffect, useState } from "react";
import { useNotificationContext } from "../NotificationProvider";
import { Outlet } from "react-router-dom";
import {
  Action,
  MakeRequest,
  NotificationLevel,
  UserNotification
} from "@durable/durable-client";
import {
  Button,
  Card,
  CircularProgress,
  Grow,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  ClearAll,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  WarningAmber as WarningAmberIcon
} from "@mui/icons-material";
import { ellipsisString } from "../components/shared/NotificationDropdown";
import { useSearchParams } from "react-router-dom";
import { useAuthContext } from "../components/shared/Auth";
import axios, { AxiosError } from "axios";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import { logging } from "../logging";

/**
 * Component that renders and handles behavior for actions.
 */
function ActionComponent({
  action,
  clearNotification,
  requestAction
}: {
  action: Action;
  clearNotification: () => void;
  requestAction: (MakeRequest) => void;
}) {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<string>(null);
  const actionData = action.action;
  const makeRequest = async () => {
    try {
      setLoading(true);
      await requestAction(actionData);
    } catch (error) {
      logging.error(error);
      const errorBody = (error as AxiosError).message;
      setErr(errorBody);
    } finally {
      setLoading(false);
      clearNotification();
    }
  };
  const props =
    "link" in action.action
      ? { href: action.action.link }
      : { onClick: makeRequest };

  const buttonColor = action.variant == "primary" ? "primary" : "inherit";
  return (
    <Tooltip title={err ? err : action.tooltip}>
      <Button
        {...props}
        color={err ? "error" : buttonColor}
        variant="text"
        size="small"
      >
        {loading ? <CircularProgress /> : action.text}
      </Button>
    </Tooltip>
  );
}

/**
 * A single notification bubble, representing a single notification
 */
function NotificationBubble({
  className: importedClass,
  id,
  level,
  title,
  body,
  actions
}: {
  className: string;
  id: string;
  level: NotificationLevel;
  title: string;
  body: string;
  actions: Array<Action>;
}) {
  const [notificationHovered, setNotificationHover] =
    useState<boolean>(false);
  const [showNotification] = useState<boolean>(true);
  const notificationSystem = useNotificationContext();
  const user = useAuthContext();

  let icon: ReactElement;
  let bgColor: string;

  // Simple truncation
  if (title.length > 30) {
    title = ellipsisString(title, 30);
  }

  const makeRequest = async (request: MakeRequest) => {
    await axios.request({
      url: request.url,
      method: request.method,
      data: request.data,
      headers: { Authorization: `Bearer ${user.token}` }
    });
  };

  /**
   * Mark a given notification read. Eventually, this method should also handle
   * an animation for notification removal.
   */
  async function clearAndMarkRead(clearAll?: boolean) {
    notificationSystem.markNotificationRead(id);
    if (clearAll) {
      notificationSystem.allNotifications?.map((notification) =>
        notificationSystem.deleteNotification(notification.id)
      );
    }
  }

  useEffect(() => {
    notificationSystem.sendNativeNotification(title);
  }, [title, notificationSystem]);

  switch (level) {
    case NotificationLevel.INFO:
      // In the future, info notifications will be able to raise images as well
      icon = null;
      bgColor = "bg-coolGrey-1000 dark:bg-coolGrey-300";
      break;
    case NotificationLevel.ERROR:
      icon = <ErrorOutlineIcon fontSize="large" className="text-error-300" />;
      bgColor = "bg-error-500 dark:bg-error-100";
      break;
    case NotificationLevel.WARNING:
      icon = (
        <WarningAmberIcon fontSize="large" className="text-warning-300" />
      );
      bgColor = "bg-warning-500 dark:bg-warning-100 ";
      break;
  }

  return (
    <div className={importedClass}>
      <Grow in={showNotification}>
        <Card
          className={`z-30 my-1 flex w-96 p-3
    shadow-xl ${bgColor} relative`}
          onMouseEnter={() => setNotificationHover(true)}
          onMouseLeave={() => setNotificationHover(false)}
        >
          <div className="absolute right-0 top-0">
            <Grow in={notificationHovered}>
              <div className="flex justify-end mx-2">
                <Tooltip title="Mark as read">
                  <IconButton
                    className="my-2"
                    size="small"
                    onClick={() => clearAndMarkRead()}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Mark all notifications as read">
                  <IconButton
                    className="my-2"
                    size="small"
                    onClick={() => clearAndMarkRead(true)}
                  >
                    <ClearAll fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </Grow>
          </div>
          {icon && (
            <div className="notification-icon mx-1 flex items-center">
              {icon}
            </div>
          )}
          <div className="notification-body mx-1 w-full">
            <div className="mb-2 text-lg">{title}</div>
            <div className="text-sm text-coolGrey-200 dark:text-coolGrey-900">
              <ReactMarkdown>{body}</ReactMarkdown>
            </div>
            <div
              className={`notification-actions flex w-full justify-end text-sm
          dark:text-coolGrey-800`}
            >
              {actions.map((action, i) => (
                <ActionComponent
                  key={`action-${i}`}
                  action={action}
                  clearNotification={clearAndMarkRead}
                  requestAction={makeRequest}
                />
              ))}
            </div>
          </div>
        </Card>
      </Grow>
    </div>
  );
}
/**
 * A stack of notifications in '3d' space.
 */
function NotificationStack({
  unreadNotifications
}: {
  unreadNotifications: Array<UserNotification>;
}) {
  const [stackHovered, setStackHovered] = useState<boolean>(false);
  const collapsedStyle = [
    "-mt-[0rem] z-30 brightness-100",
    "-mt-[7rem] z-[29] brightness-95",
    "-mt-[7rem] z-[28] brightness-90",
    "-mt-[7rem] z-[27] opacity-0",
    "-mt-[7rem] z-[27] opacity-0",
    "-mt-[7rem] z-[27] opacity-0",
    "-mt-[7rem] z-[27] opacity-0"
  ];
  const expandedStyle = "-mt-0 z-30 opacity-100 brightness-100";

  useEffect(() => setStackHovered(false), []);

  return (
    <div
      className="pointer-events-auto m-5 mt-8 flex flex-col"
      onMouseEnter={() => setStackHovered(true)}
      onMouseLeave={() => setStackHovered(false)}
    >
      {unreadNotifications?.slice(0, 6).map((notification, i) => (
        <NotificationBubble
          className={`transition-all duration-200 ease-in-out ${
            !stackHovered ? collapsedStyle[i] : expandedStyle
          }`}
          key={i}
          id={notification.id}
          level={notification.level}
          title={notification.title}
          body={notification.summary}
          actions={notification.actions}
        />
      ))}
    </div>
  );
}

/**
 * A overlayed layer on top of the page that handles displaying
 * notifications that are unread.
 *
 * One of two places notifications are displayed, the other being the
 * notification panel in NotificationPanel.tsx.
 *
 * Note that we don't have `notificationSystem` as a dependency, as this
 * causes infinite refreshes.
 */
function NotificationBubbles() {
  const notificationSystem = useNotificationContext();
  const [filterOnID, setFilterOnID] = useState<string>(null);
  const [URLParams] = useSearchParams();

  useEffect(
    () => {
      notificationSystem.startNativeNotifications();
      notificationSystem.refreshNotifications();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (URLParams.get("id")) {
      setFilterOnID(URLParams.get("id"));
    }
  }, [URLParams]);

  return (
    <div className="left-0 top-0 w-screen">
      <div
        // eslint-disable-next-line quotes
        className={`pointer-events-none fixed z-30 flex w-full justify-end`}
      >
        {notificationSystem.unreadNotifications != null && (
          <NotificationStack
            unreadNotifications={notificationSystem.getFilteredNotifications(
              filterOnID
            )}
          />
        )}
      </div>
      <Outlet />
    </div>
  );
}

export { NotificationBubbles };
